import { getModule } from 'vuex-module-decorators'
import SystemtModule from '@/store/SystemModule'
import Api from './Api'
import Model from './interface/Model'
import PaginateOptions from './interface/PaginateOptions'

export interface PublisherReconciliationBillingMonth {
  billing_month: string
  row_count: number
  total_count: number
  approved_count: number
  status: 'pending' | 'completed'
}

export interface PublisherReconciliationRow {
  id: string
  row_number: number
  first_party_impressions: number
  third_party_impressions: number
  first_party_total: number
  third_party_total: number
  delta: number
  status: 'pending' | 'approved'
}

export interface PublisherReconciliationUnmatchedRow {
  id: string
  invoice_number: string
  amount: number
  description: string
}

export interface PublisherReconciliationInvoice {
  id: string
  name: string
  uploaded_at: string
  size: number
}

export interface PublisherReconciliationDetail { }

export interface PublisherReconciliationPaginateResult {
  records: number
  data: PublisherReconciliationBillingMonth[]
}

export default class PublisherReconciliation extends Model {
  public api_settings = {
    save_mode: 'post',
    paths: {
      singular: 'publisher-reconciliation' as string | null,
      plural: 'publisher-reconciliations' as string | null,
    },
  }

  public static async detail(
    billingMonth: string,
    params: any,
  ): Promise<{ records: number; data: PublisherReconciliationDetail }> {
    const api: Api = new Api()

    return api
      .get(`publisher-reconciliations/${billingMonth}`, params)
      .then((response: any) => ({
        records: response.data.records || 0,
        data: response.data.result.publisher_reconciliations,
      }))
      .catch(PublisherReconciliation.onError)
  }

  public static async approveRow(rowId: string): Promise<void> {
    const api: Api = new Api()
    return api.patch(`publisher-reconciliation/${rowId}/approve`).then(() => undefined)
  }

  public static async reopenRow(rowId: string): Promise<void> {
    const api: Api = new Api()
    return api.patch(`publisher-reconciliation/${rowId}/reopen`).then(() => undefined)
  }

  public static async bulkApprove(ids: string[], reopen: boolean = false): Promise<void> {
    const api: Api = new Api()
    return api.post('publisher-reconciliation/bulk-approve', { ids, reopen }).then(() => undefined)
  }

  public static async pushApproved(billingMonth: string): Promise<void> {
    const api: Api = new Api()
    return api.post(`publisher-reconciliations/${billingMonth}/push`).then(() => undefined)
  }

  public static async uploadInvoice(file: File): Promise<void> {
    const api: Api = new Api()

    const formData: FormData = new FormData()

    formData.append('file', file)

    let url = 'publisher-reconciliations/invoice'

    return api
      .form(url, formData)
      .then(() => undefined)
  }

  public static async exportPeriod(
    billingMonth: string,
  ): Promise<void> {
    const api: Api = new Api()
    //  talk with Vini about this export, how it should be, with tabs [rows,invoices,unmatched_rows] or just export the rows Tab data?
    const system = getModule(SystemtModule)

    await api.post(
      `publisher-reconciliations/${billingMonth}/export`,
      {
        instance_id: system._uuid,
      },
    )
  }

  public static async getInvoices({ billingMonth, publisherId }: { billingMonth?: string | null, publisherId?: string | null }, params: any): Promise<any> {
    const api: Api = new Api()
    let url = `publisher-reconciliations/${billingMonth}/invoices`
    if (publisherId) {
      url = `publisher-reconciliations/publisher/${publisherId}/invoices`
    }
    return api
      .get(url, { ...params })
      .then((response: any) => ({
        records: response.data.result.records || 0,
        data: response.data.result.invoices || [],
      }))
  }

  public static async getUnmatches(billingMonth: string, params: any): Promise<any> {
    const api: Api = new Api()
    return api
      .get(`publisher-reconciliations/${billingMonth}/unmatched_rows`, params)
      .then((response: any) => ({
        records: response.data.result.records || 0,
        data: response.data.result.unmatched_rows || [],
      }))
  }

  private static onError(error: any): Promise<never> {
    return Promise.reject(error)
  }

  public static async downloadInvoiceById(invoiceId: string): Promise<void> {
    const system = getModule(SystemtModule)
    const api: Api = new Api()
    await api.post(`publisher-reconciliations/invoice-download/${invoiceId}`, {
      instance_id: system._uuid,
    })
  }

  public static async approveMonth(billingMonth: string): Promise<void> {
    const api: Api = new Api()
    await api.post(`publisher-reconciliations/${billingMonth}/approve`).then(() => undefined)
  }

  public static async rematch(billingMonth: string): Promise<void> {
    const api: Api = new Api()
    await api.post(`publisher-reconciliations/${billingMonth}/rematch`).then(() => undefined)
  }
}
